<script lang="ts" setup>
import { segmentation } from '@thecompaniesapi/shared/client'

const { t } = useI18n()
// const api = useApi()

// const { data: response, execute, status } = useAsyncData(
//   'doc:companies-ask',
//   () => api.companies.ask({
//     domain: segmentation.freeQueries.domain,
//     question: segmentation.freeQueries.ask,
//   }),
//   { immediate: false },
// )

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/companies/:domain/ask',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.domain'),
    example: `…/companies/${segmentation.freeQueries.domain}/ask`,
    name: 'domain',
    required: true,
    type: 'string',
  },
  {
    description: t('attributes.question'),
    example: segmentation.freeQueries.ask,
    name: 'question',
    required: true,
    type: 'string',
  },
  {
    description: [
      t('attributes.fields.description'),
      t('attributes.fields.description2'),
      t('attributes.fields.possibleValues'),
      t('attributes.fields.fieldsDescription'),
      t('attributes.fields.fieldsKey'),
      t('attributes.fields.fieldsType'),
    ],
    example: JSON.stringify([
      {
        description: 'The products that the company offers',
        key: 'products',
        type: 'array|string',
      },
    ]),
    name: 'fields',
    type: 'object',
  },
  {
    description: t('attributes.model'),
    example: 'large',
    name: 'model',
    type: 'string',
    values: ['large', 'small'],
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.answer'),
    name: 'answer',
    type: 'object',
  },
  {
    attributes: [
      {
        description: t('response.meta.cost'),
        name: 'cost',
        type: 'number',
      },
      {
        description: t('response.meta.model'),
        name: 'model',
        type: 'string',
      },
      {
        description: t('response.meta.score'),
        name: 'score',
        type: 'number',
      },
      {
        description: t('response.meta.tokens'),
        name: 'tokens',
        type: 'number',
      },
    ],
    description: t('response.meta.description'),
    name: 'meta',
    type: 'object',
  },
]

const response = {
  meta: {
    cost: 10,
    credits: 9089,
  },
  prompt: {
    context: 'company',
    feature: 'ask',
    id: 1,
    model: 'small',
    prompt: 'What products does this company offer?',
    promptKey: 'what-products-does-this-company-offer',
    response: {
      answer: {
        explanation: 'Based on company data from Microsoft and validated by external sources from SERP results.',
        output: {
          products: [
            'Microsoft Teams',
            'Microsoft Office',
            'Windows',
            'Azure',
            'Dynamics 365',
            'Syntex',
            'Lens',
            'Internet Explorer',
            'Surface',
            'Microsoft 365',
          ],
        },
        score: 90,
      },
      cost: 10,
      count: 1,
      domain: 'microsoft.com',
    },
    updatedAt: '2025-07-03T14:06:41.865+02:00',
  },
}

// async function onVisibilityChange(visibility: boolean) {
// if (visibility && !response.value) {
//   execute()
// }
// }
</script>

<template>
  <!-- :loading="status === 'pending'" -->
  <!-- :response="response?.data" -->
  <!-- @visibility-change="onVisibilityChange" -->
  <ApiDoc
    :endpoints="endpoints"
    :query-attributes="queryAttributes"
    :response="response"
    :response-attributes="responseAttributes"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "domain": "The domain name of the company you want to inquire about.",
      "fields": {
        "availableTypes": "- Supported types: `string`, `number`, `boolean`, `array`, `array|string`, `array|number`, `array|boolean`.",
        "description": "An array defining the simplified field structure for the response.",
        "description2": "If not provided, the structure will be automatically generated based on your question.",
        "fieldsDescription": "- `description`: A description explaining the field's purpose (optional)",
        "fieldsKey": "- `key`: A unique identifier string, typically in camelCase format (required)",
        "fieldsType": "- `type`: The data type of the field (required)",
        "possibleValues": "Available values for each field:"
      },
      "model": "The AI model to be used for processing the question.",
      "question": "The specific question you want to ask about the company."
    },
    "response": {
      "answer": "The answer to the question in a structured way.",
      "meta": {
        "cost": "The cost of the question.",
        "description": "The meta information about the response.",
        "model": "The model used for the question.",
        "score": "The score of the question.",
        "tokens": "The number of tokens used for the answer."
      }
    }
  },
  "es": {
    "attributes": {
      "domain": "El dominio de la empresa sobre la que quieres hacer una pregunta.",
      "fields": {
        "availableTypes": "- Tipos posibles: `string`, `number`, `boolean`, `array`, `array|string`, `array|number`, `array|boolean`.",
        "description": "Un array de definiciones de campos simplificadas para la respuesta.",
        "description2": "Si no lo proporcionas, el modelo generará un esquema de respuesta basado en tu pregunta.",
        "fieldsDescription": "- `description`: Una descripción opcional del campo.",
        "fieldsKey": "- `key`: Una cadena de caracteres, generalmente en camelCase. (required)",
        "fieldsType": "- `type`: El tipo de campo. (required)",
        "possibleValues": "Los posibles valores para cada campo:"
      },
      "model": "El modelo a utilizar para la pregunta.",
      "question": "La pregunta a hacer al modelo de IA."
    },
    "response": {
      "answer": "La respuesta a la pregunta en una forma estructurada.",
      "meta": {
        "cost": "El costo de la pregunta.",
        "description": "La información meta sobre la respuesta.",
        "model": "El modelo utilizado para la pregunta.",
        "score": "La puntuación de la pregunta.",
        "tokens": "El número de tokens usados para la respuesta."
      }
    }
  },
  "fr": {
    "attributes": {
      "domain": "Le domaine de l'entreprise sur lequel vous souhaitez poser une question.",
      "fields": {
        "availableTypes": "- Types possibles: `string`, `number`, `boolean`, `array`, `array|string`, `array|number`, `array|boolean`.",
        "description": "Un tableau définissant la structure simplifiée pour la réponse.",
        "description2": "Si non fourni, la structure sera générée automatiquement en fonction de votre question.",
        "fieldsDescription": "- `description`: Une description optionnelle du champ.",
        "fieldsKey": "- `key`: Une chaîne de caractères, généralement en camelCase. (required)",
        "fieldsType": "- `type`: Le type du champ. Peut être `string`, `number`, `boolean`, `array`, `array|string`, `array|number`, `array|boolean`.",
        "possibleValues": "Les valeurs possibles pour chaque champ:"
      },
      "model": "Le modèle à utiliser pour la question.",
      "question": "La question à poser au modèle IA."
    },
    "response": {
      "answer": "La réponse à la question en une forme structurée.",
      "meta": {
        "cost": "Le coût de la question.",
        "description": "Les informations méta sur la réponse.",
        "model": "Le modèle utilisé pour la question.",
        "score": "La note de la question.",
        "tokens": "Le nombre de tokens utilisés pour la réponse."
      }
    }
  }
}
</i18n>
